import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { AlertCircle, ArrowLeft, Play } from 'lucide-react'
import { apiJson } from '../api'
import { hasAnyRole } from '../auth'

const inputCls =
  'w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm outline-none transition-all placeholder:text-slate-400 focus:border-brand-400 focus:ring-4 focus:ring-brand-100'
const labelCls = 'mb-1.5 block text-xs font-semibold text-slate-500'

export default function PayrollRunNew() {
  const navigate = useNavigate()
  const allowManage = hasAnyRole('admin', 'hr')

  const now = new Date()
  const [form, setForm] = useState({
    period: `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`,
    orgUnitId: '',
    notes: '',
  })
  const [orgUnits, setOrgUnits] = useState<{ id: string; name: string }[]>([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!allowManage) return
    apiJson<{ id: string; name: string }[]>('/org-units')
      .then(setOrgUnits)
      .catch(() => {})
  }, [allowManage])

  const generate = async () => {
    setBusy(true)
    setError('')
    try {
      await apiJson('/payroll-runs', {
        method: 'POST',
        body: JSON.stringify({
          period: form.period,
          ...(form.orgUnitId ? { orgUnitId: form.orgUnitId } : {}),
          ...(form.notes.trim() ? { notes: form.notes.trim() } : {}),
        }),
      })
      navigate('/payroll')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Run generation failed')
      setBusy(false)
    }
  }

  if (!allowManage) {
    return (
      <div className="flex items-center gap-2.5 rounded-xl border border-amber-100 bg-amber-50 px-4 py-3 text-sm text-amber-700">
        <AlertCircle size={16} className="shrink-0" />
        Only HR or admin users can generate payroll runs.
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-xl space-y-5">
      <Link to="/payroll" className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 transition-colors hover:text-brand-600">
        <ArrowLeft size={15} /> Back to Payroll
      </Link>

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="flex items-center gap-3.5 border-b border-slate-100 px-6 py-5">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-brand-500 to-brand-700 text-white shadow-md shadow-brand-500/25">
            <Play size={20} />
          </div>
          <div>
            <h1 className="text-lg font-bold tracking-tight">New Payroll Run</h1>
            <p className="text-xs text-slate-400">Creates draft payslips from each employee's latest payslip; skips existing</p>
          </div>
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault()
            generate()
          }}
          className="space-y-4 px-6 py-6"
        >
          <div>
            <label className={labelCls}>Period *</label>
            <input required type="month" value={form.period} onChange={(e) => setForm({ ...form, period: e.target.value })} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Department (optional)</label>
            <select value={form.orgUnitId} onChange={(e) => setForm({ ...form, orgUnitId: e.target.value })} className={inputCls}>
              <option value="">Whole company</option>
              {orgUnits.map((o) => (
                <option key={o.id} value={o.id}>{o.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelCls}>Notes</label>
            <input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} maxLength={500} placeholder="Optional note…" className={inputCls} />
          </div>

          {error && (
            <div className="flex items-center gap-2.5 rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
              <AlertCircle size={16} className="shrink-0" />
              {error}
            </div>
          )}

          <div className="flex justify-end gap-3 pt-1">
            <Link
              to="/payroll"
              className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-600 transition-colors hover:bg-slate-50"
            >
              Cancel
            </Link>
            <button
              type="submit"
              disabled={busy || !form.period}
              className="rounded-xl bg-gradient-to-r from-brand-600 to-brand-700 px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-brand-500/25 transition-all hover:brightness-110 disabled:opacity-50"
            >
              {busy ? 'Generating…' : 'Generate run'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
